import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import StatCards from '../components/StatCards';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import DeleteSuccessModal from '../components/DeleteSuccessModal';
import {
  getAllAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  toggleAnnouncementStatus,
  deleteAnnouncement
} from '../services/api';
import './scrollbar.css';

/* Action Icons */
var PencilIcon = function () {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
};

var TrashIcon = function () {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
};

var ScrollbarIcon = function () {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x={3} y={4} width={18} height={5} rx={1} />
      <path d="M7 6.5h10" />
      <rect x={3} y={15} width={18} height={5} rx={1} />
      <path d="M7 17.5h5" />
    </svg>
  );
};

var ChevronLeftIcon = function () {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
};

var ChevronRightIcon = function () {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
};

var PER_PAGE_OPTIONS = [5, 10, 25, 50];


var emptyForm = {
  text: '',
  status: 'active',
  displayOrder: 1,
  scrollSpeed: 30,
  scrollDirection: 'Right to Left'
};

var extractArray = function (res) {
  if (!res) return [];
  var d = res.data ? res.data : res;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.announcements)) return d.announcements;
  if (d && Array.isArray(d.results)) return d.results;
  return [];
};

export default function ScrollbarPage() {
  var _itemsState = useState([]); var items = _itemsState[0]; var setItems = _itemsState[1];
  var _summaryState = useState(null); var summary = _summaryState[0]; var setSummary = _summaryState[1];
  var _loadingState = useState(false); var loading = _loadingState[0]; var setLoading = _loadingState[1];
  var _submittingState = useState(false); var submitting = _submittingState[0]; var setSubmitting = _submittingState[1];
  var _errorState = useState(''); var errorMsg = _errorState[0]; var setErrorMsg = _errorState[1];
  var _toastState = useState(''); var toastMsg = _toastState[0]; var setToastMsg = _toastState[1];

  var _modalState = useState(false); var isModalOpen = _modalState[0]; var setIsModalOpen = _modalState[1];
  var _editingState = useState(null); var editingItem = _editingState[0]; var setEditingItem = _editingState[1];
  var _formState = useState(emptyForm); var formData = _formState[0]; var setFormData = _formState[1];

  var _deleteTargetState = useState(null); var deleteTarget = _deleteTargetState[0]; var setDeleteTarget = _deleteTargetState[1];
  var _deleteSuccessState = useState(null); var deleteSuccess = _deleteSuccessState[0]; var setDeleteSuccess = _deleteSuccessState[1];
  var _isDeletingState = useState(false); var isDeleting = _isDeletingState[0]; var setIsDeleting = _isDeletingState[1];

  var showToast = function (msg) {
    setToastMsg(msg);
    setTimeout(function () { setToastMsg(''); }, 3500);
  };

  var fetchAnnouncements = useCallback(function () {
    setLoading(true);
    setErrorMsg('');
    getAllAnnouncements()
      .then(function (res) {
        var list = extractArray(res);
        setItems(list);
        if (res && res.data && res.data.summary) {
          setSummary(res.data.summary);
        }
      })
      .catch(function (err) {
        console.error('Failed to load announcements:', err);
        var msg = (err.response && err.response.data && err.response.data.message) || 'Failed to load announcements from server.';
        setErrorMsg(msg);
      })
      .finally(function () {
        setLoading(false);
      });
  }, [setErrorMsg, setItems, setLoading, setSummary]);

  useEffect(function () {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  var handleOpenModal = function (itemToEdit) {
    setErrorMsg('');
    if (itemToEdit) {
      setEditingItem(itemToEdit);
      setFormData({
        text: itemToEdit.text || '',
        status: itemToEdit.status || 'active',
        displayOrder: itemToEdit.displayOrder != null ? itemToEdit.displayOrder : 1,
        scrollSpeed: itemToEdit.scrollSpeed != null ? itemToEdit.scrollSpeed : 30,
        scrollDirection: itemToEdit.scrollDirection || 'Right to Left'
      });
    } else {
      setEditingItem(null);
      setFormData({
        text: '',
        status: 'active',
        displayOrder: items.length + 1,
        scrollSpeed: 30,
        scrollDirection: 'Right to Left'
      });
    }
    setIsModalOpen(true);
  };

  var handleSaveItem = function (e) {
    e.preventDefault();
    if (!formData.text.trim()) {
      alert('Announcement text is required.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    var payload = {
      text: formData.text.trim(),
      status: formData.status,
      displayOrder: Number(formData.displayOrder) || 1,
      scrollSpeed: Number(formData.scrollSpeed) || 30,
      scrollDirection: formData.scrollDirection
    };

    var id = editingItem ? (editingItem._id || editingItem.id) : null;
    var apiCall = id ? updateAnnouncement(id, payload) : createAnnouncement(payload);

    apiCall
      .then(function (res) {
        var msg = (res && res.data && res.data.message) || (id ? 'Announcement updated successfully' : 'Announcement created successfully');
        showToast(msg);
        setIsModalOpen(false);
        fetchAnnouncements();
      })
      .catch(function (err) {
        console.error('Save failed:', err);
        var msg = (err.response && err.response.data && err.response.data.message) || 'Failed to save announcement.';
        setErrorMsg(msg);
      })
      .finally(function () {
        setSubmitting(false);
      });
  };

  var handleToggleStatus = function (item) {
    var id = item._id || item.id;
    if (!id) return;

    var newStatus = item.status === 'active' ? 'inactive' : 'active';

    toggleAnnouncementStatus(id, newStatus)
      .then(function (res) {
        var msg = (res && res.data && res.data.message) || ('Announcement status changed to ' + newStatus);
        showToast(msg);
        fetchAnnouncements();
      })
      .catch(function (err) {
        console.error('Status toggle failed:', err);
        // Fallback to update call if PATCH endpoint fails
        updateAnnouncement(id, Object.assign({}, item, { status: newStatus }))
          .then(function () {
            showToast('Announcement status updated to ' + newStatus);
            fetchAnnouncements();
          })
          .catch(function (err2) {
            var msg = (err2.response && err2.response.data && err2.response.data.message) || 'Failed to change status.';
            showToast(msg);
          });
      });
  };

  var handleDeleteItem = function (item) {
    setDeleteTarget(item);
  };

  var handleDeleteConfirm = function () {
    if (!deleteTarget) return;
    var id = deleteTarget._id || deleteTarget.id;
    if (!id) return;

    setIsDeleting(true);
    deleteAnnouncement(id)
      .then(function () {
        var deleted = deleteTarget;
        setDeleteTarget(null);
        setDeleteSuccess({ id: id, text: deleted.text });
      })
      .catch(function (err) {
        console.error('Delete failed:', err);
        var msg = (err.response && err.response.data && err.response.data.message) || 'Failed to delete announcement.';
        showToast(msg);
        setDeleteTarget(null);
      })
      .finally(function () {
        setIsDeleting(false);
      });
  };

  var handleDeleteSuccessClose = function () {
    var deletedId = deleteSuccess && deleteSuccess.id;
    setDeleteSuccess(null);
    if (deletedId) {
      setItems(function (prev) {
        return prev.filter(function (it) { return (it._id || it.id) !== deletedId; });
      });
    }
    fetchAnnouncements();
  };

  var _statusFilterState = useState('all'); var statusFilter = _statusFilterState[0]; var setStatusFilter = _statusFilterState[1];
  var _currentPageState = useState(1); var currentPage = _currentPageState[0]; var setCurrentPage = _currentPageState[1];
  var _perPageState = useState(10); var itemsPerPage = _perPageState[0]; var setItemsPerPage = _perPageState[1];

  var handleFilterChange = function (newFilter) {
    setStatusFilter(newFilter);
    setCurrentPage(1);
  };

  // Sort by displayOrder ascending
  var sortedItems = items.slice().sort(function (a, b) {
    return (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0);
  });

  var activeAnnouncements = sortedItems.filter(function (it) {
    return String(it.status).toLowerCase() === 'active';
  });

  var inactiveAnnouncements = sortedItems.filter(function (it) {
    return String(it.status).toLowerCase() !== 'active';
  });

  var rtlAnnouncements = sortedItems.filter(function (it) {
    return String(it.scrollDirection || '').toLowerCase().indexOf('right') !== -1;
  });

  var filteredItems = sortedItems.filter(function (it) {
    if (statusFilter === 'active') return String(it.status).toLowerCase() === 'active';
    if (statusFilter === 'inactive') return String(it.status).toLowerCase() !== 'active';
    if (statusFilter === 'rtl') return String(it.scrollDirection || '').toLowerCase().indexOf('right') !== -1;
    return true;
  });

  var totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  var safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  useEffect(function () {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  var startIndex = (safeCurrentPage - 1) * itemsPerPage;
  var endIndex = Math.min(startIndex + itemsPerPage, filteredItems.length);
  var paginatedItems = filteredItems.slice(startIndex, endIndex);

  var getPageNumbers = function () {
    var maxVisible = 5;
    var start = Math.max(1, safeCurrentPage - Math.floor(maxVisible / 2));
    var end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    var pages = [];
    for (var p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  };

  var totalCount = summary ? summary.totalAnnouncements : sortedItems.length;
  var activeCount = summary ? summary.activeAnnouncements : activeAnnouncements.length;
  var inactiveCount = summary ? summary.inactiveAnnouncements : inactiveAnnouncements.length;
  var rtlCount = rtlAnnouncements.length;

  var firstActive = activeAnnouncements[0] || {
    text: 'No active announcements currently set',
    scrollSpeed: 30,
    scrollDirection: 'Right to Left'
  };

  var statCards = [
    {
      title: 'Total Announcements',
      value: totalCount,
      icon: 'grid',
      description: 'All announcement records',
      accent: '#38bdf8',
      active: statusFilter === 'all',
      onClick: function () { handleFilterChange('all'); }
    },
    {
      title: 'Active Announcements',
      value: activeCount,
      icon: 'check',
      description: 'Showing on storefront',
      accent: '#34d399',
      active: statusFilter === 'active',
      onClick: function () { handleFilterChange('active'); }
    },
    {
      title: 'Inactive Announcements',
      value: inactiveCount,
      icon: 'pause',
      description: 'Hidden from storefront',
      accent: '#fbbf24',
      active: statusFilter === 'inactive',
      onClick: function () { handleFilterChange('inactive'); }
    },
    {
      title: 'Right to Left Tickers',
      value: rtlCount,
      icon: 'layers',
      description: 'Scroll direction setting',
      accent: '#a78bfa',
      active: statusFilter === 'rtl',
      onClick: function () { handleFilterChange('rtl'); }
    },
  ];

  var marqueeSpeed = (firstActive.scrollSpeed || 30) + 's';
  var isLeftToRight = String(firstActive.scrollDirection).toLowerCase().indexOf('left to right') !== -1;

  return (
    <AdminLayout>
      <div className="sb-page">
        {/* Page Header */}
        <div className="page-header-row">
          <div className="page-header-main">
            <div className="page-header-title-row">
              <div className="page-header-icon">
                <ScrollbarIcon />
              </div>
              <h1 className="sb-heading">Scrollbar Banners</h1>
            </div>
            <p className="sb-subtitle">Manage top announcement bar tickers, scrolling offers, and notice text on the storefront header.</p>
          </div>
          <button className="sb-new-btn" onClick={function () { handleOpenModal(null); }}>
            + Add Announcement
          </button>
        </div>

        {/* Global Error Banner if any */}
        {errorMsg && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.12)',
            color: '#DC2626',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            padding: '12px 18px',
            borderRadius: 12,
            marginBottom: 20,
            fontSize: 13,
            fontWeight: 700
          }}>
            ⚠️ {errorMsg}
          </div>
        )}

        {/* Stat Cards Row */}
        <StatCards cards={statCards} />

        {/* Live Marquee Preview Card */}
        <div className="sb-card">
          <div className="sb-preview-header">
            <span className="sb-preview-title">
              ⚡ Storefront Ticker Live Preview
            </span>
            <span className={'sb-preview-status' + (activeAnnouncements.length === 0 ? ' disabled' : '')}>
              {activeAnnouncements.length > 0 ? '● Live (' + activeAnnouncements.length + ' Active)' : '○ No Active Announcements'}
            </span>
          </div>

          <div
            className="sb-marquee-box"
            style={{
              backgroundColor: '#0F172A',
              color: '#FEF08A'
            }}
          >
            <div
              className={'sb-marquee-track' + (activeAnnouncements.length === 0 ? ' paused' : '')}
              style={{
                animationDuration: marqueeSpeed,
                animationDirection: isLeftToRight ? 'reverse' : 'normal'
              }}
            >
              {(activeAnnouncements.length > 0 ? activeAnnouncements : [firstActive]).map(function (ann, idx) {
                return (
                  <span key={(ann._id || ann.id || idx)} className="sb-marquee-item">
                    <span>{ann.text}</span>
                  </span>
                );
              })}
              {(activeAnnouncements.length > 0 ? activeAnnouncements : [firstActive]).map(function (ann, idx) {
                return (
                  <span key={'dup-' + (ann._id || ann.id || idx)} className="sb-marquee-item">
                    <span>{ann.text}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Table Card */}
        <div className="sb-card" style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="sb-table-title" style={{ margin: 0 }}>
              {statusFilter === 'active'
                ? 'Active Announcements'
                : statusFilter === 'inactive'
                ? 'Inactive Announcements'
                : statusFilter === 'rtl'
                ? 'Right-to-Left Announcements'
                : 'All Announcements'}{' '}
              ({filteredItems.length})
            </h3>
            {loading && <span style={{ fontSize: 13, color: '#0284c7', fontWeight: 700 }}>Loading announcements…</span>}
          </div>

          <div className="sb-table-wrap">
            <table className="sb-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>S.NO</th>
                  <th>TEXT</th>
                  <th>STATUS</th>
                  <th>DISPLAY ORDER</th>
                  <th>SCROLL SPEED</th>
                  <th>SCROLL DIRECTION</th>
                  <th style={{ textAlign: 'center' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading && filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: 36, color: '#64748b', fontWeight: 700 }}>
                      Loading announcements from server…
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: 36, color: '#64748b', fontWeight: 700 }}>
                      No announcements found for this filter.
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map(function (item, index) {
                    var isAct = String(item.status).toLowerCase() === 'active';
                    var serialNo = startIndex + index + 1;
                    return (
                      <tr key={item._id || item.id || index}>
                        <td style={{ color: '#64748b', fontWeight: 700 }}>{serialNo}</td>
                        <td style={{ fontWeight: 700, color: '#0F172A', maxWidth: 360 }}>
                          {item.text}
                        </td>
                        <td>
                          <button
                            className={'sb-status-badge ' + (isAct ? 'active' : 'inactive')}
                            onClick={function () { handleToggleStatus(item); }}
                            title="Click to toggle status"
                          >
                            {isAct ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td style={{ fontWeight: 700, color: '#334155' }}>
                          {item.displayOrder != null ? item.displayOrder : 1}
                        </td>
                        <td>
                          <span className="sb-speed-tag">{item.scrollSpeed ? item.scrollSpeed + 's' : '30s'}</span>
                        </td>
                        <td style={{ fontWeight: 600, color: '#475569' }}>
                          {item.scrollDirection || 'Right to Left'}
                        </td>
                        <td className="sb-actions-cell">
                          <div className="sb-row-actions">
                            <button
                              className="sb-action-btn sb-action-edit"
                              title="Edit Announcement"
                              onClick={function () { handleOpenModal(item); }}
                            >
                              <PencilIcon />
                            </button>
                            <button
                              className="sb-action-btn sb-action-delete"
                              title="Delete Announcement"
                              onClick={function () { handleDeleteItem(item); }}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {filteredItems.length > 0 && (
            <div className="sb-pagination">
              <div className="sb-pagination-left">
                <span className="sb-pagination-info">
                  Showing <strong>{startIndex + 1}–{endIndex}</strong> of <strong>{filteredItems.length}</strong> announcements
                </span>
                <div className="sb-per-page-select">
                  <span>Rows per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={function (e) {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    {PER_PAGE_OPTIONS.map(function (opt) {
                      return (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="sb-pagination-controls">
                <button
                  type="button"
                  className="sb-page-btn"
                  disabled={safeCurrentPage <= 1}
                  onClick={function () { setCurrentPage(1); }}
                  title="First Page"
                >
                  «
                </button>
                <button
                  type="button"
                  className="sb-page-btn"
                  disabled={safeCurrentPage <= 1}
                  onClick={function () { setCurrentPage(Math.max(1, safeCurrentPage - 1)); }}
                  title="Previous Page"
                >
                  <ChevronLeftIcon />
                </button>

                {getPageNumbers().map(function (p) {
                  return (
                    <button
                      key={p}
                      type="button"
                      className={'sb-page-btn' + (p === safeCurrentPage ? ' active' : '')}
                      onClick={function () { setCurrentPage(p); }}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  type="button"
                  className="sb-page-btn"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={function () { setCurrentPage(Math.min(totalPages, safeCurrentPage + 1)); }}
                  title="Next Page"
                >
                  <ChevronRightIcon />
                </button>
                <button
                  type="button"
                  className="sb-page-btn"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={function () { setCurrentPage(totalPages); }}
                  title="Last Page"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal Dialog */}
        {isModalOpen && (
          <div className="sb-modal-overlay">
            <div className="sb-modal-box">
              <div className="sb-modal-header">
                <h3 className="sb-modal-title">{editingItem ? 'Edit Announcement' : 'Add Announcement'}</h3>
                <button className="sb-modal-close" onClick={function () { setIsModalOpen(false); }}>
                  ✕
                </button>
              </div>

              <form onSubmit={handleSaveItem}>
                <div className="sb-modal-body">
                  <div className="sb-field-group">
                    <label className="sb-label">Announcement Text *</label>
                    <textarea
                      className="sb-textarea"
                      rows="3"
                      placeholder="e.g. FREE SHIPPING ABOVE ₹5,000"
                      value={formData.text}
                      onChange={function (e) { setFormData(Object.assign({}, formData, { text: e.target.value })); }}
                      required
                    />
                  </div>

                  <div className="sb-field-row">
                    <div className="sb-field-group">
                      <label className="sb-label">Status</label>
                      <select
                        className="sb-select"
                        value={formData.status}
                        onChange={function (e) { setFormData(Object.assign({}, formData, { status: e.target.value })); }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    <div className="sb-field-group">
                      <label className="sb-label">Display Order</label>
                      <input
                        type="number"
                        min="1"
                        className="sb-input"
                        value={formData.displayOrder}
                        onChange={function (e) { setFormData(Object.assign({}, formData, { displayOrder: e.target.value })); }}
                        required
                      />
                    </div>
                  </div>

                  <div className="sb-field-row">
                    <div className="sb-field-group">
                      <label className="sb-label">Scroll Speed (seconds)</label>
                      <input
                        type="number"
                        min="5"
                        max="120"
                        className="sb-input"
                        value={formData.scrollSpeed}
                        onChange={function (e) { setFormData(Object.assign({}, formData, { scrollSpeed: e.target.value })); }}
                        required
                      />
                    </div>

                    <div className="sb-field-group">
                      <label className="sb-label">Scroll Direction</label>
                      <select
                        className="sb-select"
                        value={formData.scrollDirection}
                        onChange={function (e) { setFormData(Object.assign({}, formData, { scrollDirection: e.target.value })); }}
                      >
                        <option value="Right to Left">Right to Left</option>
                        <option value="Left to Right">Left to Right</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="sb-modal-footer">
                  <button type="button" className="sb-btn-cancel" onClick={function () { setIsModalOpen(false); }}>
                    Cancel
                  </button>
                  <button type="submit" className="sb-btn-submit" disabled={submitting}>
                    {submitting ? 'Saving...' : editingItem ? 'Save Announcement' : 'Add Announcement'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteTarget && (
          <DeleteConfirmModal
            isOpen={true}
            title="Delete Announcement"
            message="Are you sure you want to delete this announcement?"
            itemName={deleteTarget.text}
            isDeleting={isDeleting}
            onConfirm={handleDeleteConfirm}
            onCancel={function () { if (!isDeleting) setDeleteTarget(null); }}
          />
        )}

        {/* Delete Success Modal */}
        {deleteSuccess && (
          <DeleteSuccessModal
            isOpen={true}
            message="Deleted Successfully"
            subtitle="The announcement has been deleted successfully."
            onClose={handleDeleteSuccessClose}
          />
        )}

        {/* Toast */}
        {toastMsg && <div className="sb-toast">✓ {toastMsg}</div>}
      </div>
    </AdminLayout>
  );
}
